/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useSelector } from 'react-redux';

import type { AiCoach_Action } from '__generated__/graphql-types';

import { useRetracked } from 'js/lib/retracked';

import { useTracker } from '@coursera/event-pulse/react';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { COACH_RECOMMENDED_ACTION_CLICK } from 'bundles/ai-course-coach/components/Conversation/components/Messages';
import { CoachButton } from 'bundles/ai-course-coach/components/common';
import type { GlobalState } from 'bundles/ai-course-coach/store/types';
import { useCoachTrackingContext } from 'bundles/ai-course-coach/utils/tracking';
import type { SendMessageFunc } from 'bundles/ai-course-coach/utils/types';
import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';

type Props = {
  action: AiCoach_Action;
  sendMessage: SendMessageFunc;
  actionMessage: string;
  actionNotClicked: boolean;
  isRecommendedAction: boolean;
};

const Action = ({ actionMessage, action, sendMessage, actionNotClicked, isRecommendedAction }: Props) => {
  const trackRetracked = useRetracked();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;
  const { isMessageLoading } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
  }));

  return (
    <CoachButton
      disabled={isMessageLoading}
      variant="secondary"
      size="small"
      onClick={() => {
        const isSetClickTimestamp = isRecommendedAction && actionNotClicked;

        if (isSetClickTimestamp) {
          const currentDate = new Date();
          sessionStorageEx.setItem(COACH_RECOMMENDED_ACTION_CLICK, currentDate.toISOString(), (value) => value);
        }

        sendMessage(actionMessage, action);
        trackRetracked({
          trackingData: {
            input_text: actionMessage,
            timestamp: Date.now(),
          },
          trackingName: 'ai_course_coach_recommended_action',
          action: `click_send_message`,
        });

        if (trackingContext) {
          // v3 tracking
          track('interact_coach', {
            coachInteractionType: 'click_action',
            coach: {
              mode: trackingContext.mode,
              ...(trackingContext.location && { location: trackingContext.location }),
            },
            ...(trackingContext.courseId && {
              product: {
                type: 'course',
                id: trackingContext.courseId as string,
              },
            }),
            ...(trackingContext.itemId && {
              courseItem: {
                id: trackingContext.itemId as string,
              },
            }),
            coachAction: action,
          });
        }
      }}
    >
      {actionMessage}
    </CoachButton>
  );
};

export default Action;
